'use client';

import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { createContext, memo, useContext, useEffect, useReducer } from 'react';
import { Streamdown } from 'streamdown';
import { Shimmer } from '../Shimmer/Shimmer';

type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useReasoning = () => {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error('Reasoning components must be used within Reasoning');
  }
  return context;
};

export type ReasoningProps = Omit<
  ComponentProps<'div'>,
  'open' | 'defaultOpen' | 'onOpenChange'
> & {
  className?: string;
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const MS_IN_S = 1000;

/**
 * State machine for tracking duration of an active boolean.
 * - idle: value has never been true, or was true but we have no duration yet
 * - active: value is currently true, startTime captured
 * - complete: value transitioned from true to false, duration calculated
 */
type ActiveDurationState =
  | { status: 'idle' }
  | { status: 'active'; startTime: number }
  | { status: 'complete'; durationSeconds: number };

type ActiveDurationAction =
  | { type: 'start'; startTime: number }
  | { type: 'complete'; durationSeconds: number };

function activeDurationReducer(
  state: ActiveDurationState,
  action: ActiveDurationAction
): ActiveDurationState {
  switch (action.type) {
    case 'start':
      return { status: 'active', startTime: action.startTime };
    case 'complete':
      return { status: 'complete', durationSeconds: action.durationSeconds };
    default:
      return state;
  }
}

/**
 * Tracks how long a boolean value stays true.
 * Returns undefined while active, duration in seconds after completion.
 */
function useActiveDuration(isActive: boolean): number | undefined {
  const [state, dispatch] = useReducer(activeDurationReducer, { status: 'idle' });

  useEffect(() => {
    if (isActive && state.status !== 'active') {
      // idle|complete → active
      dispatch({ type: 'start', startTime: Date.now() });
    } else if (!isActive && state.status === 'active') {
      // active → complete
      const durationSeconds = Math.ceil((Date.now() - state.startTime) / MS_IN_S);
      dispatch({ type: 'complete', durationSeconds });
    }
  }, [isActive, state]);

  return state.status === 'complete' ? state.durationSeconds : undefined;
}

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open,
    defaultOpen = true,
    onOpenChange,
    duration: durationProp,
    children,
    ...props
  }: ReasoningProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });

    const computedDuration = useActiveDuration(isStreaming);
    const duration = durationProp ?? computedDuration;

    return (
      <ReasoningContext.Provider value={{ isStreaming, isOpen, setIsOpen, duration }}>
        <div className={cn('not-prose mb-2', className)} {...props}>
          {children}
        </div>
      </ReasoningContext.Provider>
    );
  }
);

export type ReasoningTriggerProps = Omit<ComponentProps<'button'>, 'children'> & {
  children?: ReactNode;
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
};

const defaultGetThinkingMessage = (isStreaming: boolean, duration?: number) => {
  if (isStreaming) {
    return <Shimmer duration={1}>Thinking</Shimmer>;
  }
  if (duration === undefined) {
    return <p>Thought</p>;
  }
  return <p>Thought for {duration} seconds</p>;
};

export const ReasoningTrigger = memo(
  ({
    className,
    children,
    getThinkingMessage = defaultGetThinkingMessage,
    ...props
  }: ReasoningTriggerProps) => {
    const { isStreaming, duration, isOpen, setIsOpen } = useReasoning();

    return (
      <button
        className={cn(
          'flex w-full items-center justify-between gap-2 text-muted-foreground text-xs font-medium transition-colors hover:text-foreground',
          className
        )}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        {...props}
      >
        {children ?? (
          <>
            <span className="min-w-0">{getThinkingMessage(isStreaming, duration)}</span>
            {isOpen ? (
              <ChevronUpIcon className="size-3.5 shrink-0" />
            ) : (
              <ChevronDownIcon className="size-3.5 shrink-0" />
            )}
          </>
        )}
      </button>
    );
  }
);

export type ReasoningContentProps = Omit<ComponentProps<'div'>, 'children'> & {
  children: string;
};

export const ReasoningContent = memo(({ className, children, ...props }: ReasoningContentProps) => {
  const { isOpen } = useReasoning();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'mt-1 max-h-[120px] overflow-y-auto text-muted-foreground text-[11px] leading-tight font-semibold',
        '[&_*]:text-[11px] [&_*]:leading-tight [&_*]:font-semibold [&_*]:text-muted-foreground',
        '[&_p]:my-0.5',
        className
      )}
      {...props}
    >
      <Streamdown>{children}</Streamdown>
    </div>
  );
});

Reasoning.displayName = 'Reasoning';
ReasoningTrigger.displayName = 'ReasoningTrigger';
ReasoningContent.displayName = 'ReasoningContent';
